import type { CSSProperties } from 'react'
import type { MemberNavStyle, ShowcaseConfiguration } from '@/types/theme'

/**
 * Helper to extract logo URL from ShowcaseConfiguration
 */
export function getLogoFromConfig(config?: ShowcaseConfiguration, variant: 'header' | 'form' = 'header'): string | undefined {
  if (!config) return undefined

  const root =
    typeof config.logo === 'string' && config.logo.trim().length > 0
      ? config.logo.trim()
      : undefined

  const branding = config.login?.branding
  const headerUrl = branding?.header?.logo?.url?.trim()
  const bodyLogo =
    typeof branding?.body?.logo === 'string' && branding.body.logo.trim().length > 0
      ? branding.body.logo.trim()
      : undefined
  const formUrl = branding?.form?.logo?.url?.trim()

  // Brand Studio grava o logo na raiz (`logo`); fluxos legados usam header/body/form.
  if (variant === 'header') {
    return headerUrl || root || bodyLogo || formUrl || undefined
  }

  // Form / card de login: prioriza body + form (showcase antigo), depois header e raiz.
  return bodyLogo || formUrl || headerUrl || root || undefined
}

/**
 * Helper to extract favicon URL from ShowcaseConfiguration
 */
export function getFaviconFromConfig(config?: ShowcaseConfiguration): string | undefined {
  return config?.login?.branding?.header?.favicon || config?.seo?.favicon
}

/**
 * Helper to extract login background image from ShowcaseConfiguration
 */
export function getLoginBackgroundFromConfig(config?: ShowcaseConfiguration): string | undefined {
  return config?.login?.layout?.body?.image
}

/**
 * Helper to extract home/vitrine body background image from ShowcaseConfiguration
 */
export function getHomeBackgroundFromConfig(config?: ShowcaseConfiguration): string | undefined {
  return getHomeBodyImageFromConfig(config)
}

/**
 * Resolve a imagem de fundo da home no formato novo ou legado (cache antigo).
 */
export function getHomeBodyImageFromConfig(
  config?: ShowcaseConfiguration | null,
): string | undefined {
  if (!config) return undefined

  const direct = config.home?.layout?.body?.image
  if (typeof direct === 'string' && direct.trim()) return direct.trim()

  const legacy = (config as unknown as Record<string, unknown>).showcase_colors as
    | Record<string, unknown>
    | undefined
  const legacyBody = legacy?.home as Record<string, unknown> | undefined
  const legacyImage = legacyBody?.body as Record<string, unknown> | undefined
  const legacyBg = legacyImage?.backgroundImage
  if (typeof legacyBg === 'string' && legacyBg.trim()) return legacyBg.trim()

  return undefined
}

/**
 * Converts a raw image URL (or existing url() value) to a valid CSS background-image value.
 */
export function toCssBackgroundImage(value?: string | null): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  if (!trimmed || trimmed === 'none') return undefined
  if (trimmed.startsWith('url(')) return trimmed
  return `url(${trimmed})`
}

/**
 * Inline styles for pages that use the home body background (vitrine / bem-vindo).
 */
export function getHomeBodyBackgroundStyle(
  config?: ShowcaseConfiguration | null,
): CSSProperties {
  const body = config?.home?.layout?.body
  const backgroundImage = toCssBackgroundImage(getHomeBodyImageFromConfig(config))

  return {
    backgroundColor: body?.color ? body.color : 'var(--body-bg, #1D1D20)',
    // Fallback em CSS vars: theme-init aplica antes da hidratação do React.
    backgroundImage: backgroundImage || 'var(--body-bg-image, none)',
    backgroundSize: body?.imageFill || 'var(--body-bg-fill, cover)',
    backgroundPosition: body?.imagePosition || 'var(--body-bg-position, center)',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'fixed',
  }
}

/**
 * Helper to extract company name from ShowcaseConfiguration
 */
export function getCompanyNameFromConfig(config?: ShowcaseConfiguration, fallback: string = 'Mentorfy'): string {
  // Try to infer from metadata or use fallback
  return config?.metadata?.migratedFromOldStructure ? fallback : fallback
}

/**
 * Helper to check if a configuration has valid content
 */
export function hasValidConfig(config?: ShowcaseConfiguration): boolean {
  if (!config) return false
  return !!(config.login || config.home || config.mentoring || config.register)
}

const isEnabledFlag = (value: unknown): boolean => value === true || value === 'true'

/**
 * Estilo de navegação da área do aluno escolhido no Brand Studio.
 * Qualquer valor diferente de "side-menu" (inclusive ausente) mantém a barra no topo.
 */
export function getMemberNavStyle(config?: ShowcaseConfiguration | null): MemberNavStyle {
  return config?.home?.layout?.navigation?.style === 'side-menu' ? 'side-menu' : 'top-bar'
}

/** Seção "Tarefas" no menu do aluno: só aparece quando ligada no Brand Studio. */
export function isTasksSectionEnabled(config?: ShowcaseConfiguration | null): boolean {
  return isEnabledFlag(config?.home?.features?.tasks?.enabled)
}

/**
 * Selos da vitrine: ligados por padrão. Só desligam com `false` explícito vindo
 * do Brand Studio — por isso não usam isEnabledFlag, que trata ausente como off.
 */
const isEnabledUnlessDisabled = (value: unknown): boolean =>
  value !== false && value !== 'false'

/** Selo "Novo" nos cards de produto da vitrine. */
export function isNewBadgeEnabled(config?: ShowcaseConfiguration | null): boolean {
  return isEnabledUnlessDisabled(config?.home?.features?.badges?.new)
}

/** Selo "Em Alta" nos cards de produto da vitrine. */
export function isTrendingBadgeEnabled(config?: ShowcaseConfiguration | null): boolean {
  return isEnabledUnlessDisabled(config?.home?.features?.badges?.trending)
}

/** Seção "Mensagens" no menu do aluno: mesma flag que já libera /mensagens. */
export function isMessagingSectionEnabled(config?: ShowcaseConfiguration | null): boolean {
  return isEnabledFlag(config?.home?.features?.messaging?.allowMessages)
}
